import { Divider, Table } from 'antd'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PerButton from '../../../components/custom_components/Button'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { PerPaymentCycleDropDown } from '../../../components/custom_components/PaymentCycleDropDown'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import PerText from '../../../components/custom_components/Text'
import { useAuth } from '../../../context/auth-context'
import { useCurrency } from '../../../context/currency-context'
import useMutateOneOffPayment from '../../../graphql/one-off-payment/hooks/useMutateOneOffPayment'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { useDateTime } from '../../../shared/useDateTime'
import { OneOffPayment } from '../../../types'
import { OneOffPaymentModal } from './OneOffPaymentModal'
import { IconIndicator } from '../../../components/custom_components/IconIndicator/IconIndicator'

interface Props {
  meetingId: number
  showNotOnFirstOfMonthDisclaimer: boolean
  oneOffPayment?: OneOffPayment
  nextPaymentcycle: string
  hasDivider?: boolean
  isOneOffPaymentNotAllowed: boolean
}

export const EditableOneOffPayment: FC<Props> = ({
  meetingId,
  showNotOnFirstOfMonthDisclaimer,
  oneOffPayment,
  nextPaymentcycle,
  hasDivider = false,
  isOneOffPaymentNotAllowed,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const { currency } = useCurrency()
  const { utcDateStringToMoment } = useDateTime(user)
  const [isModalOpen, toggleModalOpen] = useState<boolean>(false)
  const [isDeleting, toggleDeleting] = useState<boolean>(false)

  const { tableActionButtonsRowWidth } = tableActionButtons()
  const { saveOneOffPayment } = useMutateOneOffPayment(meetingId)

  const changePaymentCycle = (utcDate: Date) => {
    if (oneOffPayment && oneOffPayment.resultAmount) {
      saveOneOffPayment({
        meetingId: meetingId,
        comment: oneOffPayment.comment,
        paymentDate: utcDate.toUTCString(),
        resultAmount: oneOffPayment.resultAmount,
        id: oneOffPayment.id,
      })
    }
  }

  const activeOneOffPayment = oneOffPayment ? oneOffPayment : null

  const columns = [
    {
      key: 'reason',
      title: t('appraisalMeeting.oneOffPayment.reason'),
      dataIndex: 'comment',
      className: 'textAlign-left minWidth-column-xl',
      render: (dataIndex: number) => <PerText fontWeight="bold">{dataIndex}</PerText>,
    },
    {
      className: 'textAlign-left minWidth-column-xl',
    },
    {
      key: 'monetaryPaid',
      title: t('okr.objective.monetary.toBePaid'),
      dataIndex: 'resultAmount',
      className: 'textAlign-right minWidth-column-xxl',
      render: (dataIndex: number) => {
        return dataIndex && <PerText color="primary">{formatCurrency(dataIndex, currency)}</PerText>
      },
    },
    {
      key: 'action',
      title: t('okr.objective.actions'),
      className: 'textAlign-left minWidth-column-s',
      dataIndex: 'id',
      width: tableActionButtonsRowWidth(2),
      render: () => (
        <PerTableActionButtons
          buttons={[
            {
              icon: 'edit_fill',
              color: 'primary',
              tooltip: t('tooltip.edit'),
              showOnlyWhen: !isOneOffPaymentNotAllowed,
              onClick: (e: any) => {
                e.preventDefault()
                toggleModalOpen(true)
              },
              disabled: isDeleting,
            },
            {
              icon: 'delete_fill',
              color: 'error',
              tooltip: t('tooltip.delete'),
              onClick: async (e: any) => {
                e.preventDefault()
                toggleDeleting(true)
                toggleModalOpen(true)
              },
              isProcessing: { active: isDeleting },
            },
          ]}
          data-e2e-delete-oneoffpayment
        />
      ),
    },
  ]

  const tableData: (OneOffPayment & { key: string })[] =
    activeOneOffPayment === null ? [] : [{ ...activeOneOffPayment, key: activeOneOffPayment.id }]

  return (
    <div data-e2e-one-off-payment-container>
      <OneOffPaymentModal
        open={isModalOpen}
        isDeleting={isDeleting}
        toggleOpen={toggleModalOpen}
        toggleDeleting={toggleDeleting}
        oneOffPayment={activeOneOffPayment === null ? undefined : activeOneOffPayment}
        meetingId={meetingId}
        nextPaymentcycle={nextPaymentcycle}
      />

      <PerFlexRow justify="space-between" align="middle" mb="s">
        <PerFlexRow align="middle">
          <PerText appearance="heading" color="primary" mr={activeOneOffPayment ? 's' : undefined}>
            {t('appraisalMeeting.oneOffPayment.title') + (activeOneOffPayment ? ' ' + t('appraisalMeeting.oneOffPayment.titleOn') : '')}
          </PerText>

          {activeOneOffPayment && !isOneOffPaymentNotAllowed && (
            <PerPaymentCycleDropDown
              value={activeOneOffPayment.paymentDate}
              earliestCycle={nextPaymentcycle}
              onChange={(utcDateChanged: string) => {
                changePaymentCycle(utcDateStringToMoment(utcDateChanged)!.toDate())
              }}
            />
          )}

          {activeOneOffPayment && isOneOffPaymentNotAllowed && (
            <IconIndicator
              icon="warning_fill"
              background="error"
              variant="clickable"
              size="l"
              tooltip={t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingOneOffPaymentsForLowEmployee')}
            />
          )}
        </PerFlexRow>

        {!activeOneOffPayment && (
          <PerButton
            text={t('appraisalMeeting.oneOffPayment.createButton')}
            before="add_stroke"
            onClick={() => toggleModalOpen(true)}
            color="primary"
            disabled={isOneOffPaymentNotAllowed}
            tooltip={isOneOffPaymentNotAllowed ? t('appraisalMeeting.oneOffPayment.paymentButtonDisabled') : ''}
            data-e2e-add-oneoffpayment
          />
        )}
      </PerFlexRow>

      <PerText color="secondary" mb="s">
        {showNotOnFirstOfMonthDisclaimer
          ? t('appraisalMeeting.oneOffPayment.descriptionWithNotOnFirstOfMonthDisclaimer')
          : t('appraisalMeeting.oneOffPayment.description')}
      </PerText>

      {activeOneOffPayment && <Table dataSource={tableData} columns={columns} pagination={false} />}
      {hasDivider && <Divider />}
    </div>
  )
}
